<template>
  <div class="login-container">
    <router-link
      v-if="showLogoLink"
      to="/"
      class="logo-link"
      aria-label="Home page"
    >
      <img src="/images/eddy.svg" alt="" aria-hidden="true" />
    </router-link>
    <img
      src="/images/background-cap.svg"
      alt=""
      class="background-cap"
      aria-hidden="true"
    />
    <div class="left-panel">
      <div class="title-container">
        <div class="title-wrapper">
          <h1 class="title">Welcome Back!</h1>
          <p class="mobile-signup-text">
            New to Eddy?
            <button class="link-style" @click="goToSignup">Sign up here</button>
          </p>
        </div>
      </div>

      <div class="form-inputs">
        <BaseInput
          type="text"
          label="Email*"
          placeholder="Input Email"
          v-model="email"
          autocomplete="email"
          class="input-field"
          :error="isEmailError"
          :errorMessage="emailErrorMessage"
          :showInputError="false"
        />

        <div class="password-wrapper">
          <BaseInput
            type="password"
            label="Password*"
            placeholder="Input Password"
            v-model="password"
            autocomplete="current-password"
            class="input-field"
            :error="isPasswordError"
            :errorMessage="passwordErrorMessage"
            :showInputError="false"
          />
          <div
            v-if="errorMessage"
            class="error-message"
            role="alert"
            aria-live="assertive"
          >
            {{ errorMessage }}
          </div>
        </div>
      </div>

      <div class="remember-forgot-container">
        <div class="remember-forgot-wrapper">
          <div class="remember-me-wrapper">
            <v-checkbox
              id="remember-me-checkbox"
              v-model="rememberMe"
              label="Remember Me"
              hide-details
              color="var(--primary-blue)"
              class="remember-checkbox"
            ></v-checkbox>
            <!-- Target for Vuetify's auto-generated aria-describedby attribute to satisfy WCAG -->
            <span
              id="remember-me-checkbox-messages"
              style="display: none"
            ></span>
          </div>

          <router-link
            v-if="enableRedirect"
            :to="{ name: 'ForgotPasswordPage' }"
            class="forgot-password-link hover-underline"
          >
            Forgot Password?
          </router-link>
          <button
            v-else
            @click="goToForgotPassword"
            class="forgot-password-button hover-underline"
          >
            Forgot Password?
          </button>
        </div>
      </div>

      <div class="action-buttons">
        <AuthButton
          @click="login"
          :loading="loading"
          :showDisabledStyle="isFormEmpty"
          text="Sign In"
          className="login-button"
        />

        <div class="or-divider">
          <span class="line"></span>
          <span class="text">Or</span>
          <span class="line"></span>
        </div>

        <div class="social-buttons">
          <button class="google-button" @click="loginWithGoogle">
            <img
              src="/images/google-logo.svg"
              alt="Google Icon"
              aria-hidden="true"
            />
            Continue with Google
          </button>
          <button class="facebook-button" disabled>
            <img
              src="/images/facebook.svg"
              alt="Facebook Icon"
              class="facebook-icon"
              aria-hidden="true"
            />
            Continue with Facebook
          </button>
        </div>
        <div v-if="showGuestLogin" class="guest-login-divider-container">
          <div class="guest-login-divider">
            <span class="guest-login-divider-line"></span>
            <span class="guest-login-divider-text">Or login as a guest</span>
            <span class="guest-login-divider-line"></span>
          </div>
          <BaseButton
            variant="outline"
            class="guest-login-button"
            @click="goToGuestLogin"
          >
            Continue as Guest</BaseButton
          >
        </div>
      </div>
    </div>

    <!-- right side -->
    <div v-if="showRightPanel" class="right-panel">
      <div class="signup-content">
        <img
          src="/images/login-illustration.svg"
          class="illustration"
          alt=""
          aria-hidden="true"
        />
        <p class="signup-title">Sign up for Eddy!</p>
        <BaseButton variant="white" class="signup-button" @click="goToSignup">
          New to Eddy? Sign up here!
        </BaseButton>
        <p class="login-form-description" v-if="descriptionText">
          {{ descriptionText }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import BaseButton from "@/components/common/BaseButton.vue";
import AuthButton from "@/components/common/AuthButton.vue";
import { useUserState } from "@/composeables/useUserState";
import { isAxiosError } from "axios";
import {
  computed,
  defineEmits,
  defineProps,
  onMounted,
  onUnmounted,
  ref,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import authService from "../services/authService";
import cookiesService from "../services/cookiesService";
import BaseInput from "./BaseInput.vue";
import { getSafeAuthRedirect } from "@/utils/authRedirect";
import { startGoogleLogin } from "@/services/googleLoginService";

// Props
const props = defineProps({
  // Controls whether to show the right promotional panel
  showRightPanel: {
    type: Boolean,
    default: true,
  },
  // Controls whether to show the logo link in top-left
  showLogoLink: {
    type: Boolean,
    default: true,
  },
  // Controls whether to redirect after successful login
  enableRedirect: {
    type: Boolean,
    default: true,
  },
  // Default redirect URL if no redirect query param is provided
  defaultRedirectUrl: {
    type: String,
    default: "/",
  },
  // Controls whether to load saved credentials from localStorage
  loadSavedCredentials: {
    type: Boolean,
    default: true,
  },
  // Controls whether to show error message from query params
  showQueryError: {
    type: Boolean,
    default: true,
  },
  // Controls whether to use popup mode for Google login (for modal usage)
  usePopupMode: {
    type: Boolean,
    default: false,
  },
  // Controls whether to show guest login
  showGuestLogin: {
    type: Boolean,
    default: false,
  },
  descriptionText: {
    type: String,
    default: "",
  },
});

// Emits
const emit = defineEmits([
  "login-success",
  "login-error",
  "goto-signup",
  "google-login",
  "goto-forgot-password",
  "goto-guest-login",
]);

const router = useRouter();
const route = useRoute();
const { updateUserState } = useUserState();

const email = ref("");
const password = ref("");
const rememberMe = ref(false);
const loading = ref(false);
const isEmailError = ref(false);
const emailErrorMessage = ref("");
const isPasswordError = ref(false);
const passwordErrorMessage = ref("");
const errorMessage = ref("");
let stopGoogleLogin = () => {};

const isFormEmpty = computed(() => {
  return !email.value.trim() || !password.value.trim();
});

function validateForm() {
  isEmailError.value = !email.value;
  emailErrorMessage.value = isEmailError.value ? "Email required" : "";

  isPasswordError.value = !password.value;
  passwordErrorMessage.value = isPasswordError.value ? "Password required" : "";

  return !(isEmailError.value || isPasswordError.value);
}

function handleEmailVerificationRequired() {
  localStorage.setItem("registeredEmail", email.value);
  if (props.enableRedirect) {
    router.push({ name: "EmailVerificationPage" });
  }
}

async function login() {
  if (!validateForm()) {
    return;
  }

  loading.value = true;
  try {
    const data = {
      email: email.value,
      password: password.value,
      rememberMe: rememberMe.value,
    };
    const response = await authService.login(data);

    if (response.status === 200) {
      const data = response?.data?.data;
      const userData = data?.user;

      if (userData) {
        await cookiesService.setCookie("authToken", {
          token: data.token,
          role: data.role,
          ...userData,
        });
      }

      try {
        localStorage.removeItem("savedPassword");
        if (rememberMe.value) {
          localStorage.setItem("savedEmail", email.value);
        } else {
          localStorage.removeItem("savedEmail");
        }
      } catch {
        console.warn(
          "Browser storage unavailable; email could not be remembered."
        );
      }

      // Update global user state in Vuex store
      await updateUserState(userData);

      // Emit success event with user data
      emit("login-success", {
        userData,
        token: data.token,
        role: data.role,
      });

      // Handle redirect only if enabled
      if (props.enableRedirect) {
        const redirectTo = route.query.redirect || props.defaultRedirectUrl;
        handleRedirect(redirectTo);
      }
    } else {
      const errorMsg = response?.data?.message;

      if (errorMsg === "Please verify your account") {
        handleEmailVerificationRequired();
        return;
      }

      errorMessage.value = errorMsg;
      emit("login-error", errorMsg);
    }
  } catch (error) {
    let errorMsg = "Something went wrong.";
    const errorResponse = error?.response?.data?.data;

    if (isAxiosError(error)) {
      if (error?.response?.status >= 400 && error?.response?.status <= 499) {
        errorMsg = error?.response?.data?.message;
      } else {
        errorMsg = "Something went wrong.";
      }
    } else {
      errorMsg = "Incorrect password or email.";
    }

    if (
      errorMsg === "Please verify your account" ||
      errorResponse?.redirectToVerification
    ) {
      handleEmailVerificationRequired();
      return;
    }

    errorMessage.value = errorMsg;
    emit("login-error", errorMsg);
  } finally {
    loading.value = false;
  }
}

function loginWithGoogle() {
  emit("google-login");
  stopGoogleLogin();
  stopGoogleLogin = startGoogleLogin({
    redirectTo: route.query.redirect || props.defaultRedirectUrl,
    usePopupMode: props.usePopupMode,
    onSuccess: handleGoogleLoginSuccess,
  });
}

function goToSignup() {
  emit("goto-signup");

  // Default behavior if no custom handler is provided
  if (props.enableRedirect) {
    router.push("/signup");
  }
}

function goToForgotPassword() {
  emit("goto-forgot-password");

  // Default behavior if no custom handler is provided
  if (props.enableRedirect) {
    router.push("/forgot-password");
  }
}

function goToGuestLogin() {
  emit("goto-guest-login");

  // Default behavior if no custom handler is provided
  if (props.enableRedirect) {
    router.push("/guest-login");
  }
}

function handleRedirect(redirectTo) {
  router.push(getSafeAuthRedirect(redirectTo, props.defaultRedirectUrl));
}

async function handleGoogleLoginSuccess(result) {
  try {
    // The callback has already established the existing auth cookie.
    const authToken = await cookiesService.getCookie("authToken");
    if (!authToken?.token) throw new Error("Google login session unavailable");
    await updateUserState();
    emit("login-success", {
      token: authToken.token,
      isMergedAccount: result.isMergedAccount,
    });
    if (props.enableRedirect) {
      handleRedirect(result.redirectTo);
    }
  } catch {
    console.error("Failed to complete Google login.");
    emit("login-error", "Failed to complete Google login");
  }
}

onMounted(() => {
  // Retain the existing prop and email autofill without persisting passwords.
  try {
    localStorage.removeItem("savedPassword");
    if (props.loadSavedCredentials) {
      const savedEmail = localStorage.getItem("savedEmail");
      if (savedEmail) {
        email.value = savedEmail;
        rememberMe.value = true;
      }
    }
  } catch {
    console.warn(
      "Browser storage unavailable; saved email could not be loaded."
    );
  }

  // Show query error message if enabled
  if (props.showQueryError) {
    const queryErrorMessage = route.query["error-message"];
    if (queryErrorMessage && !errorMessage.value) {
      errorMessage.value = queryErrorMessage;
    }
  }
});

onUnmounted(() => {
  stopGoogleLogin();
});
</script>

<style scoped>
/* Main Container */
.login-container {
  display: flex;
  height: 100vh;
}

.background-cap {
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: -999;
}

/* Left Panel */
.left-panel {
  width: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
}

.logo-link {
  display: flex;
  gap: 19px;
  margin-top: 40px;
  margin-left: 40px;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 50;
}

.title-container {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 24px;
}

.title-wrapper {
  width: 508px;
  display: flex;
  flex-direction: column;
  justify-content: start;
}

.title {
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 1rem;
  color: var(--primary-blue);
}

.mobile-signup-text {
  display: none;
}

/* Form Inputs */
.form-inputs {
  display: flex;
  gap: 24px;
  width: 100%;
  flex-direction: column;
  align-items: center;
}

:deep(.input-field) {
  width: 508px !important;
  text-align: left;
  color: var(--black-body-text) !important;
  font-size: var(--fs-large);
  line-height: 24px !important;
}

:deep(.input-field input) {
  width: 100% !important;
  text-align: left;
  font-size: var(--fs-big);
}

:deep(.input-field .input-wrap) {
  width: 508px !important;
}

.password-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.error-message {
  text-align: left;
  color: #e00269;
  margin-top: 10px;
  width: 508px;
  overflow-wrap: break-word;
  word-break: break-word;
}

/* Remember Me & Forgot Password */
.remember-forgot-container {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 5px;
}

.remember-forgot-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 508px;
}

.remember-me-wrapper {
  display: flex;
  align-items: center;
}

.remember-checkbox {
  color: var(--primary-blue);
  font-size: 1rem;
  border-radius: 10px;
  margin-left: -8px;
}

:deep(.remember-checkbox .v-label) {
  font-size: var(--fs-small);
  font-style: normal;
  font-weight: 700;
  color: var(--black-body-text);
  opacity: 1;
}

.forgot-password-link {
  color: #e00069;
  font-size: var(--fs-small);
  font-weight: 400;
  cursor: pointer;
}

.forgot-password-button {
  background: none;
  border: none;
  color: #e00069;
  font-size: var(--fs-small);
  font-weight: 400;
  cursor: pointer;
  padding: 0;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
}

.login-button {
  width: 508px;
}

.social-buttons {
  display: flex;
  gap: 10px;
  align-items: center;
  width: 508px;
}

.google-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 50px;
  border: 1px solid var(--primary-blue);
  color: var(--primary-blue);
  background-color: white;
  font-size: var(--fs-big);
  border-radius: 12px;
  font-weight: 500;
  flex: 1;
  cursor: pointer;
}

.google-button img {
  width: 20px;
  height: 20px;
}

.facebook-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 50px;
  border: 1px solid var(--border-color);
  background-color: white;
  border-radius: 12px;
  color: var(--border-color);
  font-size: var(--fs-big);
  font-weight: 500;
  flex: 1;
}

.facebook-button:disabled {
  cursor: not-allowed;
  color: var(--neutral-700);
}

.facebook-icon {
  filter: grayscale(100%);
  width: 20px;
  height: 20px;
}

/* Guest Login */
.guest-login-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 20px 0;
}

.guest-login-divider-text {
  font-size: var(--fs-small);
  color: var(--neutral-700);
  font-weight: 400;
}

.guest-login-divider-line {
  flex: 1;
  height: 1px;
  border-top: 2px solid var(--border-color);
}

.guest-login-button {
  width: 508px;
  font-weight: 600;
}

/* Right Panel */
.right-panel {
  width: 50%;
  background-color: var(--primary-blue);
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
}

.language-button {
  padding: 8px;
  width: 60px;
  font-size: var(--fs-small);
  cursor: pointer;
  border: none;
}

.language-button-en {
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
}

.language-button-es {
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
}

.language-button.active {
  background: #e00069;
  color: white;
}

.language-button:not(.active) {
  background: #f2f3fb;
  color: #e00069;
}

.signup-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.illustration {
  width: 50%;
}

.signup-title {
  color: white;
  font-size: 32px;
  font-weight: 600;
  margin-top: 32px;
}

.signup-button {
  width: 508px;
  margin-top: 32px;
  font-size: var(--fs-big);
  font-weight: 700;
}

.login-form-description {
  font-size: var(--fs-small);
  color: #ffffff;
  font-weight: 400;
  margin-top: 20px;
}

input[type="checkbox"].custom-checkbox {
  width: 1.5em;
  height: 1.5em;
  accent-color: #d62467;
  margin-right: 8px;
  border: 2px solid #d62467;
  border-radius: 4px;
}

/* Or Divider */
.or-divider {
  display: flex;
  align-items: center;
  width: 508px;
  margin: 20px 0;
  gap: 12px;
}

.or-divider .line {
  flex: 1;
  height: 1px;
  background-color: var(--border-color);
}

.or-divider .text {
  color: #3b4648;
  font-size: var(--fs-small);
  white-space: nowrap;
}

@media (max-width: 768px) {
  .login-container {
    height: fit-content;
  }

  .right-panel {
    display: none;
  }

  .left-panel {
    width: 100%;
    padding: 16px;
    margin-top: 70px;
  }

  .mobile-signup-text {
    display: block;
    font-size: var(--fs-small);
    margin-bottom: 16px;
  }

  .mobile-signup-text a {
    color: var(--primary-blue);
    text-decoration: underline;
  }

  .password-wrapper {
    width: 100%;
  }

  :deep(.input-field) {
    width: 100% !important;
    text-align: left;
  }

  :deep(.input-field input) {
    width: 100% !important;
  }

  :deep(.input-field .input-wrap) {
    width: 100% !important;
  }

  .login-button {
    width: 100%;
  }

  .social-buttons {
    width: 100%;
    flex-direction: column;
  }

  .google-button,
  .facebook-button {
    width: 100%;
    padding: 10px 0px;
  }

  .logo-link {
    margin-left: 16px;
    margin-top: 16px;
  }

  .guest-login-divider-container {
    width: 100%;
  }

  .guest-login-button {
    width: 100%;
  }
}
</style>
